import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SellerDashboardLayout from '../../components/SellerDashboardLayout';
import RequestInquireModal from '../../components/RequestInquireModal';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { rfqApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import Pagination from '../../components/Pagination/Pagination';
import './RFQManagePage.css';

const RFQManagePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission, canViewContactsForRegion, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const [selectedRFQs, setSelectedRFQs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState([]); // Track expanded cards on mobile
  // Initialize status filter from URL if present
  const [statusFilter, setStatusFilter] = useState(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl) {
      return statusFromUrl.charAt(0).toUpperCase() + statusFromUrl.slice(1).toLowerCase();
    }
    return 'All';
  });
  const [countryFilter, setCountryFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [rfqData, setRfqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRFQDetail, setSelectedRFQDetail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [replyLoading, setReplyLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const itemsPerPage = 8;

  // Calculate active filter count for badge
  const activeFilterCount = [countryFilter, statusFilter, categoryFilter].filter(f => f !== 'All').length;

  // Update status filter when URL changes
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl) {
      setStatusFilter(statusFromUrl.charAt(0).toUpperCase() + statusFromUrl.slice(1).toLowerCase());
    }
  }, [searchParams]);

  // Check canReceiveRFQs permission after subscription loads
  useEffect(() => {
    // Wait for subscription to load before checking permission
    if (!subscriptionLoading && !hasPermission('canReceiveRFQs')) {
      setShowUpgradeModal(true);
    }
  }, [hasPermission, subscriptionLoading]);

  const fetchRFQs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (statusFilter !== 'All') {
        params.status = statusFilter.toLowerCase();
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }

      let data;
      // Get sellerId from user object (could be sellerId or sellerProfileId)
      const userSellerId = user?.sellerId || user?.sellerProfileId;
      console.log('User object:', user);
      console.log('User sellerId:', userSellerId);
      
      // If user is a seller, fetch RFQs for their seller profile
      if (userSellerId) {
        console.log('Fetching RFQs for sellerId:', userSellerId);
        const response = await rfqApi.getBySellerId(userSellerId, params);
        // Handle response structure: { rfqs: [...], permissions: {...}, total: X }
        data = response?.rfqs || response || [];
        console.log('RFQ response:', response);
      } else if (user?.id) {
        // Fetch RFQs with userId filter
        console.log('Fetching RFQs for userId:', user.id);
        params.userId = user.id;
        data = await rfqApi.getAll(params);
      } else {
        console.log('Fetching all RFQs');
        data = await rfqApi.getAll(params);
      }
      console.log('RFQ Data received:', data);
      setRfqData(Array.isArray(data) ? data : data?.rfqs || []);
    } catch (err) {
      console.error('Error fetching RFQs:', err);
      setError('Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, user]);

  useEffect(() => {
    fetchRFQs();
  }, [fetchRFQs]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRFQs(filteredData.map(r => r.id));
    } else {
      setSelectedRFQs([]);
    }
  };

  const handleSelectRFQ = (rfqId) => {
    if (selectedRFQs.includes(rfqId)) {
      setSelectedRFQs(selectedRFQs.filter(id => id !== rfqId));
    } else {
      setSelectedRFQs([...selectedRFQs, rfqId]);
    }
  };

  const handleStatusChange = async (rfqId, newStatus) => {
    try {
      await rfqApi.updateStatus(rfqId, newStatus);
      fetchRFQs();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRFQs.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedRFQs.length} RFQ(s)?`)) {
      try {
        await rfqApi.bulkDelete(selectedRFQs);
        setSelectedRFQs([]);
        fetchRFQs();
      } catch (err) {
        console.error('Error deleting RFQs:', err);
      }
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRFQs.length === 0) return;
    
    try {
      await rfqApi.bulkUpdateStatus(selectedRFQs, status);
      setSelectedRFQs([]);
      fetchRFQs();
    } catch (err) {
      console.error('Error updating RFQs:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    }).replace(/\//g, ',');
  };

  // Toggle card expansion for mobile view
  const toggleCardExpand = (rfqId) => {
    setExpandedCards(prev => 
      prev.includes(rfqId) 
        ? prev.filter(id => id !== rfqId) 
        : [...prev, rfqId]
    );
  };

  // Get unique countries for filter
  const countries = [...new Set(rfqData.map(r => r.country).filter(Boolean))];
  
  // Get unique categories for filter
  const categories = [...new Set(rfqData.map(r => r.category).filter(Boolean))];

  // Helper function to check if buyer contact can be viewed for a specific RFQ
  const canViewBuyerContact = (rfq) => {
    return hasPermission('canViewBuyerContacts') && canViewContactsForRegion(rfq?.country);
  };

  // Mask email for display in table if user doesn't have permission
  const maskEmailForDisplay = (email, rfq) => {
    if (!email) return '-';
    if (canViewBuyerContact(rfq)) return email;
    const [name, domain] = email.split('@');
    if (!domain) return '***@***.***';
    return `${name.substring(0, 2)}${'*'.repeat(Math.max(name.length - 2, 3))}@***.***`;
  };

  // Filter data based on all filters
  const filteredData = rfqData.filter(r => {
    const matchCountry = countryFilter === 'All' || r.country === countryFilter;
    const matchCategory = categoryFilter === 'All' || r.category === categoryFilter;
    return matchCountry && matchCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <SellerDashboardLayout>
      <div className="rfq-manage-page">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-title-section">
            <h1 className="page-title">RFQ Manage</h1>
            <p className="page-description">
              Easily Track, Edit, and Respond to Buyer Quotations in One Place
            </p>
          </div>
        </div>

        {/* RFQ List Section */}
        <div className="rfq-list-section">
          {/* List Header */}
          <div className="list-header">
            <h2 className="list-title">RFQ Lists</h2>
            <div className="list-controls">
              {/* Search Box */}
              <div className="search-box">
                <input 
                  type="text" 
                  placeholder="Search" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.167 14.167L17.5003 17.5003" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667Z" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Mobile Filter Toggle Button */}
              <button 
                className="mobile-filter-toggle"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="currentColor"/>
                </svg>
                <span>Filters</span>
                {activeFilterCount > 0 && !showMobileFilters && (
                  <span className="filter-badge">{activeFilterCount}</span>
                )}
                <svg 
                  className={`chevron-icon ${showMobileFilters ? 'expanded' : ''}`}
                  width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Collapsible Filters Container */}
              <div className={`filters-collapsible ${showMobileFilters ? 'expanded' : ''}`}>
                {/* Country Filter - Native Select */}
                <div className="select-wrapper">
                  <select 
                    className="filter-select"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                    style={{ backgroundColor: '#F5F5F5', color: '#969696' }}
                  >
                    <option value="All">Country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                
                {/* Status Filter - Native Select */}
                <div className="select-wrapper">
                  <select 
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ backgroundColor: '#F5F5F5', color: '#969696' }}
                  >
                    <option value="All">Status</option>
                    <option value="New">New</option>
                    <option value="Replied">Replied</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                
                {/* Filter By Category - Native Select */}
                <div className="select-wrapper category-select">
                  <svg className="filter-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955ZM11.4218 10.2016C11.3136 10.3162 11.2528 10.4675 11.2514 10.625V15.2078L8.75144 16.875V10.625C8.75149 10.4663 8.69116 10.3135 8.58269 10.1977L3.12644 4.37502H16.8764L11.4218 10.2016Z" fill="#969696"/>
                  </svg>
                  <select 
                    className="filter-select with-icon"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{ backgroundColor: '#F5F5F5', color: '#969696' }}
                  >
                    <option value="All">Filter By Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Bulk Actions */}
                {selectedRFQs.length > 0 && (
                  <div className="bulk-actions">
                    <button className="bulk-btn replied" onClick={() => handleBulkStatusUpdate('replied')}>
                      Mark Replied
                    </button>
                    <button className="bulk-btn closed" onClick={() => handleBulkStatusUpdate('closed')}>
                      Mark Closed
                    </button>
                    <button className="bulk-btn delete" onClick={handleBulkDelete}>
                      Delete ({selectedRFQs.length})
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RFQ Table */}
          {loading ? (
            <div className="loading-state">Loading RFQs...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              <p>No RFQs found</p>
              <span>RFQs submitted by buyers will appear here</span>
            </div>
          ) : (
            <>
              <div className="rfq-table-figma">
                {/* Table Header - Figma Design */}
                <div className="figma-table-header">
                  <div className="figma-col figma-col-checkbox">
                    <label className="custom-checkbox-wrapper">
                      <input 
                        type="checkbox"
                        checked={selectedRFQs.length === filteredData.length && filteredData.length > 0}
                        onChange={() => handleSelectAll(selectedRFQs.length !== filteredData.length)}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <span>All</span>
                  </div>
                  <div className="figma-col figma-col-date">Date</div>
                  <div className="figma-col figma-col-buyer">Buyer Name</div>
                  <div className="figma-col figma-col-company">Company Type</div>
                  <div className="figma-col figma-col-category">Categoy</div>
                  <div className="figma-col figma-col-subcategory">Sub Category</div>
                  <div className="figma-col figma-col-location">Location</div>
                  <div className="figma-col figma-col-email">Email</div>
                  <div className="figma-col figma-col-status">Statues</div>
                  <div className="figma-col figma-col-action">Action</div>
                </div>

                {/* Table Body - Figma Design */}
                <div className="figma-table-body">
                  {paginatedData.map((rfq) => (
                    <div key={rfq.id} className={`figma-table-row ${expandedCards.includes(rfq.id) ? 'expanded' : ''}`}>
                      {/* Checkbox */}
                      <div className="figma-col figma-col-checkbox">
                        <label className="custom-checkbox-wrapper">
                          <input 
                            type="checkbox"
                            checked={selectedRFQs.includes(rfq.id)}
                            onChange={() => handleSelectRFQ(rfq.id)}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </div>

                      {/* Date */}
                      <div className="figma-col figma-col-date">{formatDate(rfq.createdAt)}</div>

                      {/* Buyer Name */}
                      <div className="figma-col figma-col-buyer">{rfq.fullName}</div>

                      {/* Company Type */}
                      <div className="figma-col figma-col-company">{rfq.companyName}</div>

                      {/* Category */}
                      <div className="figma-col figma-col-category">{rfq.category}</div>

                      {/* Sub Category */}
                      <div className="figma-col figma-col-subcategory">{rfq.subCategory || '-'}</div>

                      {/* Location */}
                      <div className="figma-col figma-col-location">{rfq.country}</div>

                      {/* Email */}
                      <div className="figma-col figma-col-email" title={canViewBuyerContact(rfq) ? rfq.email : 'Upgrade plan to view buyer contact'}>
                        {maskEmailForDisplay(rfq.email, rfq)}
                      </div>

                      {/* Status */}
                      <div className="figma-col figma-col-status">
                        <span className={`figma-status-badge ${rfq.status?.toLowerCase() || 'new'}`}>
                          {rfq.status ? rfq.status.charAt(0).toUpperCase() + rfq.status.slice(1) : 'New'}
                        </span>
                      </div>

                      {/* Action */}
                      <div className="figma-col figma-col-action">
                        <span 
                          className="figma-details-link"
                          onClick={() => setSelectedRFQDetail(rfq)}
                        >
                          Details
                        </span>
                      </div>

                      {/* Mobile Expand Button */}
                      <button 
                        className="figma-card-expand-btn"
                        onClick={() => toggleCardExpand(rfq.id)}
                        aria-expanded={expandedCards.includes(rfq.id)}
                        aria-label={expandedCards.includes(rfq.id) ? 'Collapse details' : 'Expand details'}
                      >
                        <span>{expandedCards.includes(rfq.id) ? 'Less' : 'More'}</span>
                        <svg 
                          className={`expand-icon ${expandedCards.includes(rfq.id) ? 'expanded' : ''}`}
                          width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </div>

        {/* RFQ Detail Modal - using RequestInquireModal */}
        <RequestInquireModal
          isOpen={!!selectedRFQDetail}
          onClose={() => setSelectedRFQDetail(null)}
          inquireData={selectedRFQDetail || {}}
          isSubscribed={hasPermission('canViewBuyerContacts') && canViewContactsForRegion(selectedRFQDetail?.country)}
          onStatusChange={handleStatusChange}
          isLoading={replyLoading}
          onReply={async (replyData) => {
            if (!user?.id) {
              alert('Please login to reply');
              return;
            }
            
            // Use sellerId or sellerProfileId if available, otherwise fall back to user.id
            const sellerIdToUse = user?.sellerId || user?.sellerProfileId || user?.id;
            
            try {
              setReplyLoading(true);
              const result = await rfqApi.reply(
                replyData.rfqId,
                sellerIdToUse,
                replyData.message
              );
              
              console.log('RFQ Reply sent, conversation created:', result);
              
              // Close modal and refresh list
              setSelectedRFQDetail(null);
              fetchRFQs();
              
              // Navigate to messages with the new conversation
              if (result.conversationId) {
                navigate(`/messages?conversation=${result.conversationId}`);
              }
            } catch (err) {
              console.error('Error sending RFQ reply:', err);
              // Check if it's a messaging permission error
              if (err.code === 'MESSAGING_NOT_ALLOWED') {
                alert('Messaging is only available with Global Enterprise plan. Please upgrade to reply to RFQs and message buyers.');
                navigate('/membership-packages');
              } else {
                alert('Failed to send reply. Please try again.');
              }
            } finally {
              setReplyLoading(false);
            }
          }}
          onUpgradePlan={() => {
            navigate('/membership-packages');
          }}
        />
      </div>

      {/* Upgrade Prompt Modal for RFQ permission */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate(-1);
        }}
        feature="Receive RFQs"
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="GCC Business"
      />
    </SellerDashboardLayout>
  );
};

export default RFQManagePage;
